import { abbreviationFromState } from '@contracts/order/valueNormalizers/State';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { FormTextInput } from '@src/common/components/FormTextInput';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { FormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  OwnerAddressField,
  OwnerCityField,
  OwnerCompanyNameField,
  OwnerEmailField,
  OwnerPhoneField,
  OwnerStateField,
  OwnerZipCodeField,
  TractorIsLeasedField,
  tractorOwnerFields,
  TractorOwnerNameField,
  USDOTNumberOfOwningCompanyField,
} from '@src/permitField/TractorOwnerFields';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type TractorLicensePlate = FormEntries<string[]>;

type Props = { fields: string[] } & CollapseListener;

export const TractorOwnerSection = (props: Props) => {
  const { t } = useTranslation();
  const { isFieldDisabled } = useDisabledFields();
  const {
    register,
    unregister,
    control,
    formState: { errors, disabled: formDisabled },
    watch,
    getValues,
  } = useFormContext<TractorLicensePlate>();

  const isLeased = watch(TractorIsLeasedField.fieldName);

  const { sectionInError } = useSectionError(Object.keys(tractorOwnerFields));

  if (props.fields.length <= 0) {
    return null;
  }
  const onUncheckIsLeased = () => {
    Object.keys(tractorOwnerFields).forEach((ownerField) => {
      if (ownerField === TractorIsLeasedField.fieldName) return;

      unregister(ownerField);
    });
  };

  const onIsLeasedChanged = () => {
    const isChecked = getValues(TractorIsLeasedField.fieldName);
    if (isChecked) return;
    onUncheckIsLeased();
  };
  return (
    <Stack gap={theme.spacing['3']}>
      <ConditionalField
        fields={props.fields}
        conditions={TractorIsLeasedField.fieldName}
      >
        <FormCheckbox
          disabled={
            formDisabled || isFieldDisabled(TractorIsLeasedField.fieldName)
          }
          register={register}
          errors={errors}
          fieldName={TractorIsLeasedField.fieldName}
          onChange={onIsLeasedChanged}
          defaultChecked={getValues(TractorIsLeasedField.fieldName) as boolean}
        />
      </ConditionalField>
      {isLeased && (
        <EditOrderCollapsible
          title={t('leaseInformation')}
          id="tractor-owner-information"
          collapseEventName={props.collapseEventName}
          uncollapseEventName={props.uncollapseEventName}
          inError={sectionInError}
        >
          <Stack gap={theme.spacing['3']}>
            <Stack direction="column" gap={theme.spacing['3']}>
              <ConditionalField
                fields={props.fields}
                conditions={TractorOwnerNameField.fieldName}
              >
                <FormTextInput
                  register={register}
                  errors={errors}
                  fieldName={TractorOwnerNameField.fieldName}
                  disabled={isFieldDisabled(TractorOwnerNameField.fieldName)}
                />
              </ConditionalField>

              <ConditionalField
                fields={props.fields}
                conditions={OwnerCompanyNameField.fieldName}
              >
                <FormTextInput
                  register={register}
                  errors={errors}
                  fieldName={OwnerCompanyNameField.fieldName}
                  disabled={isFieldDisabled(OwnerCompanyNameField.fieldName)}
                />
              </ConditionalField>
              <ConditionalField
                fields={props.fields}
                conditions={USDOTNumberOfOwningCompanyField.fieldName}
              >
                <FormTextInput
                  register={register}
                  errors={errors}
                  fieldName={USDOTNumberOfOwningCompanyField.fieldName}
                  disabled={isFieldDisabled(
                    USDOTNumberOfOwningCompanyField.fieldName,
                  )}
                />
              </ConditionalField>
              <Stack direction="column" gap={theme.spacing['3']}>
                <ConditionalField
                  fields={props.fields}
                  conditions={OwnerAddressField.fieldName}
                >
                  <FormTextInput
                    register={register}
                    errors={errors}
                    fieldName={OwnerAddressField.fieldName}
                    disabled={isFieldDisabled(OwnerAddressField.fieldName)}
                  />
                </ConditionalField>
                <Stack direction="row" gap={theme.spacing['3']}>
                  <ConditionalField
                    fields={props.fields}
                    conditions={OwnerCityField.fieldName}
                  >
                    <FormTextInput
                      register={register}
                      errors={errors}
                      fieldName={OwnerCityField.fieldName}
                      disabled={isFieldDisabled(OwnerCityField.fieldName)}
                    />
                  </ConditionalField>

                  <ConditionalField
                    fields={props.fields}
                    conditions={OwnerStateField.fieldName}
                  >
                    <FormDropdown
                      control={control}
                      fieldName={OwnerStateField.fieldName}
                      options={OwnerStateField.options.map((option) => ({
                        label: abbreviationFromState(option),
                        value: option,
                      }))}
                      disabled={isFieldDisabled(OwnerStateField.fieldName)}
                    />
                  </ConditionalField>

                  <ConditionalField
                    fields={props.fields}
                    conditions={OwnerZipCodeField.fieldName}
                  >
                    <FormTextInput
                      register={register}
                      errors={errors}
                      fieldName={OwnerZipCodeField.fieldName}
                      disabled={isFieldDisabled(OwnerZipCodeField.fieldName)}
                    />
                  </ConditionalField>
                </Stack>
                <Stack direction="row" gap={theme.spacing['3']}>
                  <ConditionalField
                    fields={props.fields}
                    conditions={OwnerPhoneField.fieldName}
                  >
                    <FormTextInput
                      register={register}
                      errors={errors}
                      fieldName={OwnerPhoneField.fieldName}
                      disabled={isFieldDisabled(OwnerPhoneField.fieldName)}
                    />
                  </ConditionalField>
                  <ConditionalField
                    fields={props.fields}
                    conditions={OwnerEmailField.fieldName}
                  >
                    <FormTextInput
                      register={register}
                      errors={errors}
                      fieldName={OwnerEmailField.fieldName}
                      disabled={isFieldDisabled(OwnerEmailField.fieldName)}
                    />
                  </ConditionalField>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </EditOrderCollapsible>
      )}
    </Stack>
  );
};
